import { Vector2 } from "three";
import { FrameFeatureBox, isDefined, isNull, PanoFrameFeatureBox } from "../../infra3d";
import { Obj } from "../internal";
/**
 * Feature that is only displayed on a specific frame (framenumber,streamid,segmentid)
 * @class FrameFeature
 * @extends {Obj}
 * @constructor
 */
export class FrameFeature extends Obj {
    constructor(parentcollection) {
        super();
        this.frameFeatureBox = null;
        this.highlighton = false;
        this.type = "FrameFeature";
        this.id = null;
        this.properties = {};
        this._isInScene = false;
        this.infrascene = parentcollection.scene.infrascene;
        this.parentcollection = parentcollection;
        this.scene = parentcollection.scene;
        this.properties = parentcollection.getDefaultProps();
    }
    /**
     * sets properties of the framefeature with the values in the json object
     * @param f framefeature as json Object
     * @param needsUpdate true if framefeature has to displayed on the current frame;
     * set false if framefeature was created in a different frame then its supposed to be
     */
    parseFromJSON(f, needsUpdate = true) {
        if (f["type"] == "Feature") {
            this.type = f["type"];
            if (isDefined(f["properties"])) {
                if (this.properties) {
                    for (const property in f["properties"]) {
                        this.properties[property] = f["properties"][property];
                    }
                }
                else {
                    this.properties = f["properties"];
                }
                this.category = f["properties"]["category"];
                this.nx = f["properties"]["nx"];
                this.ny = f["properties"]["ny"];
                this.w = f["properties"]["w"];
                this.h = f["properties"]["h"];
                if (this.properties["projection"] == "panoramic") {
                    this.calloteId = parseInt(this.properties.pos);
                    this.frameFeatureBox = new PanoFrameFeatureBox(this.properties.nx, this.properties.ny, this.properties.w, this.properties.h, this.calloteId);
                }
                else {
                    this.frameFeatureBox = new FrameFeatureBox(this.properties.nx, this.properties.ny, this.properties.w, this.properties.h);
                }
                this.updateStyle();
            }
            if (isDefined(f["id"]))
                this.id = f["id"];
        }
        if (needsUpdate) {
            let activeimage = null;
            if (f["properties"]["projection"] == "perspective") {
                activeimage = f["properties"]["pos"] == 0 ? "left" : "right";
            }
            this.update(f["properties"]["framenumber"], f["properties"]["segmentid"], f["properties"]["streamid"], activeimage);
        }
    }
    toGeoJsonString() {
        const geoJSON = this.toGeoJSON();
        return JSON.stringify(geoJSON);
    }
    toGeoJSON() {
        const jsonobject = {};
        jsonobject["type"] = "Feature";
        jsonobject["properties"] = this.properties;
        jsonobject["id"] = this.id;
        jsonobject["geometry"] = {
            type: "Point",
            coordinates: [0, 0, 0],
        };
        return jsonobject;
    }
    /**
     * Returns all FrameFeatures that were drawn together with this (for PanoFrameFeatureBox)
     *
     * @returns {FrameFeature[]}
     */
    findSiblings() {
        return this.parentcollection.findSiblings(this.properties.objectid, this.id);
    }
    destroy() {
        this.type = null;
        this.id = null;
        this.properties = null;
        this.parentcollection = null;
        this.scene = null;
        this.highlighton = false;
        if (this.frameFeatureBox)
            this.infrascene.remove(this.frameFeatureBox);
        this.frameFeatureBox = null;
    }
    /**
     * Recalculates the corners and size of the FrameFeatureBox
     *
     * @param p1 movepoint, opposite point that should be current mouse coordinates
     * @param p2 anchorpoint, this should always be the same (first click)
     */
    resize(p1, p2) {
        const mapcoordsSmallest = new Vector2();
        const mapcoordsBiggest = new Vector2();
        if (p1.x < p2.x) {
            mapcoordsSmallest.x = p1.x;
            mapcoordsBiggest.x = p2.x;
        }
        else {
            mapcoordsSmallest.x = p2.x;
            mapcoordsBiggest.x = p1.x;
        }
        if (p1.y < p2.y) {
            mapcoordsSmallest.y = p1.y;
            mapcoordsBiggest.y = p2.y;
        }
        else {
            mapcoordsSmallest.y = p2.y;
            mapcoordsBiggest.y = p1.y;
        }
        const w = mapcoordsBiggest.x - mapcoordsSmallest.x;
        const h = mapcoordsBiggest.y - mapcoordsSmallest.y;
        this.w = w;
        this.h = h;
        this.nx = mapcoordsSmallest.x;
        this.ny = mapcoordsSmallest.y;
        this.properties.w = Math.ceil(w * 10000) / 10000;
        this.properties.h = Math.ceil(h * 10000) / 10000;
        this.properties.nx = Math.ceil(mapcoordsSmallest.x * 10000) / 10000;
        this.properties.ny = Math.ceil(mapcoordsSmallest.y * 10000) / 10000;
        this.frameFeatureBox.resize(this.nx, this.ny, this.w, this.h);
    }
    /**
     * loads FrameFeature for current Frame, Segment and Stream
     * @param currentFrameNum
     * @param currentSegmentId
     * @param currentStreamId
     */
    update(currentFrameNum, currentSegmentId, currentStreamId, activeimage) {
        if (this.frameFeatureBox != null) {
            if (this.properties.framenumber == currentFrameNum &&
                this.properties.segmentid == currentSegmentId &&
                this.properties.streamid == currentStreamId) {
                this.infrascene.addForFrame(this.frameFeatureBox);
                this._isInScene = true;
                //hide FrameFeatures from the not shown image in StereoStreams or right images in Streams
                if (activeimage) {
                    if ((this.properties.pos == 0 && activeimage == "right") ||
                        (this.properties.pos == 1 && activeimage == "left") ||
                        this.properties.projection == "panoramic")
                        this.hide();
                    else
                        this.show();
                }
                else {
                    if (this.properties.projection == "perspective" && this.properties.pos == 1)
                        this.hide();
                    else
                        this.show();
                }
            }
            else {
                if (this._isInScene)
                    this.infrascene.removeForFrame(this.frameFeatureBox);
                this._isInScene = false;
            }
        }
    }
    add() {
        this.infrascene.addForFrame(this.frameFeatureBox);
    }
    remove() {
        this.infrascene.removeForFrame(this.frameFeatureBox);
    }
    show() {
        this.frameFeatureBox.show();
    }
    hide() {
        this.frameFeatureBox.hide();
    }
    isVisible() {
        return this.frameFeatureBox.isVisible();
    }
    highlightOn() {
        if (!this.highlighton && this.frameFeatureBox) {
            this.frameFeatureBox.highlightOn();
            this.highlighton = true;
        }
    }
    highlightOff() {
        if (this.highlighton && this.frameFeatureBox) {
            this.frameFeatureBox.highlightOff();
            this.highlighton = false;
        }
    }
    /**
     * finds box at mx,my position and adds it to pickedfeatures
     *
     * @param mx
     * @param my
     * @param pickedfeatures
     */
    pick(mx, my, pickedfeatures) {
        if (!isNull(this.frameFeatureBox) && this._isInScene) {
            const picked = this.frameFeatureBox.pick(mx, my);
            if (picked.length > 0)
                pickedfeatures.push(this);
        }
    }
    /**
     * Sets the color value to given category (needs rendering to take effect)
     */
    updateStyle() {
        const matdef = this.parentcollection.style.toMaterialDefinitions(this.properties);
        this.frameFeatureBox.updateStyle(matdef);
    }
}
//# sourceMappingURL=framefeature.js.map